/** The in-store register: till, ticket, payment, pickups and receipts. */
const pos = {
  en: {
    loading: "Opening the register…",
    catalogError: "Could not load the catalog. Check the connection and retry.",
    saleFailed: "The sale could not be recorded. Check the ticket and try again.",
    offlineQueued: {
      one: "📴 {count} sale saved on this device — syncing when the connection is back",
      other: "📴 {count} sales saved on this device — syncing when the connection is back",
    },
    oversell: {
      title: "Out of stock",
      message:
        "{name} shows no stock left. Sell it anyway? Its stock will go negative so you can correct the count later.",
      confirm: "Sell anyway",
    },
    clear: {
      title: "Clear the ticket?",
      message: "Every line on the current ticket will be removed.",
      confirm: "Clear ticket",
    },
    header: {
      drawer: "Cash drawer",
      supplier: "A supplier's delivery or payment",
      register: "Register",
      today: "today",
      todayAria: "Today: {sales}, {amount}. Open today's sales",
      pickups: "Orders waiting to be collected",
      pickupsDue: {
        one: "{count} pickup due today",
        other: "{count} pickups due today",
      },
      held: "Held tickets",
      lock: "Lock the till",
      lockAs: "Lock the till ({name})",
      showActions: "Show the buttons — {count} waiting",
      manage: "Manage store (admin login)",
      manageShort: "Manage",
    },
    lock: {
      title: "Unlock the till",
      prompt: "Enter your PIN to open the till",
      notRecognised: "That PIN is not recognised",
      checkFailed: "Could not check that PIN — is the connection up?",
      checking: "Checking…",
      deleteDigit: "Delete last digit",
      noPin:
        "No PIN yet? The owner gives each person one under Manage store → Staff.",
      manage: "⚙️ Manage store",
      registerFallback: "Register",
    },
    pickups: {
      title: "Waiting to collect",
      loadFailed: "Could not load waiting orders.",
      empty: "Nothing waiting to be collected.",
      customer: "Customer",
      at: "{day} at {time}",
      dueToday: "Due today",
      late: { one: "{count} day late", other: "{count} days late" },
      confirm: "Take {amount} and hand it over?",
      paidBy: "Paid by",
      handedOver: "Handed over",
      notYet: "Not yet",
      handOver: "Hand this over",
      collectFailed: "Could not hand that over — check the stock is there.",
      statusPreparing: "Preparing",
      statusReady: "Ready",
      markReady: "Mark ready",
      markReadyFailed: "Could not mark that ready — try again.",
    },
    ticket: {
      title: "Current ticket",
      hold: "⏸ Hold",
      clear: "🗑 Clear",
      empty: "Empty ticket",
      emptyHint: "Tap a product to start the sale",
      notInStock: "not in stock",
      remove: "remove",
      removeOne: "Remove one {name}",
      addOne: "Add one {name}",
      subtotal: "Subtotal · {items}",
      offers: { one: "{count} offer", other: "{count} offers" },
      discount: "Discount",
      none: "None",
      total: "Total",
    },
    payment: {
      cashReceived: "Cash received",
      change: "Change",
      exact: "Exact",
      short: "Cash received is less than the total",
      creditNote: "Nothing is paid now — this adds to their tab.",
      creditPartialNote: "Part is paid now — the rest adds to their tab.",
      creditFor: "On credit for {name}.",
      creditReceivedNow: "Received now",
      creditStays: "Stays on credit",
      showKeypad: "Show keypad",
      hideKeypad: "Hide keypad",
      typeCash: "Type the cash received",
      complete: "Complete sale",
      saving: "Saving…",
    },
    cart: {
      noItems: "No items yet",
      tapProduct: "Tap a product to start",
      review: "Tap to review ticket",
      charge: "Charge",
    },
    customer: {
      add: "Add customer card",
      purchasesBefore: {
        one: "{count} purchase here before",
        other: "{count} purchases here before",
      },
      firstPurchase: "First purchase here",
      owes: "Owes {amount}",
      linked: "Linked to this sale",
      scanTitle: "Customer card",
      notRecognised: "That card is not recognised",
      checkFailed: "Could not check that card",
      nobody: "Nobody found with that number",
      searchFailed: "Could not search",
      pointCamera: "Point at the customer's code",
      cameraDenied: "Camera access was not given — type the code instead.",
      cameraUnsupported: "This device has no usable camera — type the code instead.",
      cardCode: "Card code",
      codePlaceholder: "8 characters",
      find: "Find",
      phoneDigits: "…or the last digits of their phone",
      phonePlaceholder: "e.g. 5432",
      searchButton: "Search",
      typeInstead: "Type it instead",
      useCamera: "Use the camera",
      skip: "Skip",
    },
    search: {
      placeholder: "Search product, brand or flavour…",
      clear: "Clear search",
    },
    grid: {
      noMatch: "No product matches",
      nothingFor: "Nothing found for “{term}”",
      emptyCategory: "This category has no products yet",
      shelfEmpty: "Your shelf is empty",
      shelfEmptyHint: "Add your products first, then come back here to sell them. Picking from the catalogue takes a minute.",
      addProducts: "Add products",
    },
    packChoice: {
      subtitle: "Pick what the customer wants for each part.",
      included: "Included",
      chooseOne: "Choose one",
      chooseMany: "Choose {count}",
      picked: "{picked} of {total}",
      left: "{count} left",
      out: "Out",
      less: "Remove one {name}",
      add: "Add to ticket",
      pickAll: "Pick everything first",
    },
    group: {
      open: "Open {name}, {count} variants",
      variants: { one: "{count} variant", other: "{count} variants" },
      attention: "{count} low or out",
      from: "from",
      inStock: "{count} in stock",
      summary: {
        one: "{count} variant · {stock} in stock",
        other: "{count} variants · {stock} in stock",
      },
      removeOne: "Remove one {name}",
      done: "Done",
      doneWithCount: "Done · {count} added",
    },
    tile: {
      add: "Add {name}, {price}",
      out: "Out",
      left: "{count} left",
      offer: "OFFER",
      buyGet: "Buy {buy}, get {get} free",
      packOf: { one: "Pack · {count} item", other: "Pack · {count} items" },
    },
    belowCost: {
      title: "Price too low",
      message: "At this discount, {names} would sell for less than the shop paid.",
      askOwner: "An owner can approve this sale with their PIN.",
      ownerOnly: "Only an owner can approve this.",
      changeDiscount: "Change the discount",
    },
    supplier: {
      title: "Supplier",
      subtitle: "Stock that came in, or money paid out of the drawer.",
      delivery: "Delivery",
      payOnly: "Pay only",
      who: "Supplier",
      whoPlaceholder: "Who is it from?",
      unknown: "Pick a supplier from the list to pay them.",
      searchProduct: "Find a product…",
      new: "New",
      lotTotal: "Lot total ({currency})",
      amount: "Amount paid from the drawer ({currency})",
      paidFromDrawer: "Paid now, from the drawer ({currency})",
      paidHint: "Leave empty if nothing was paid.",
      note: "Note",
      recordDelivery: "Record delivery",
      recordPayout: "Record payment",
      received: "Delivery recorded — stock is updated.",
      paid: "Payment to {name} recorded.",
      failed: "Could not record that.",
    },
    drawer: {
      title: "Cash drawer",
      openSince: "Open since {time} · {name}",
      openTitle: "Open the drawer",
      openHint: "Count the cash in the drawer and type the total before the first sale.",
      open: "Open the drawer",
      shouldHold: "Should be in the drawer",
      lines: {
        opening: "Counted at opening",
        sales: { one: "Cash from {count} sale", other: "Cash from {count} sales" },
      },
      movements: {
        cash_in: "Change put in",
        withdrawal: "Taken out",
        refund: "Cash refund",
        tab_payment: "Tab paid back",
        supplier_payout: "Supplier paid",
      },
      reasonTitle: "What is it for?",
      reasons: {
        float: "Change for the till",
        owner_investment: "My own money",
        from_safe: "From my savings",
        expense: "Pay an expense",
        supplier: "Pay a supplier",
        to_safe: "Move to my savings",
        personal_use: "Personal use",
        correction: "Correction",
      },
      reasonHints: {
        float: "Coins and notes to give change. Not income.",
        owner_investment: "Money you add from your own pocket. Not a sale.",
        from_safe: "Taken from your savings. Your savings go down by this much.",
        expense: "Rent, electricity, cleaning… Counted as an expense, so net revenue goes down.",
        supplier: "Opens the supplier payment, so what you owe them goes down.",
        to_safe: "Cash you take away to keep. It becomes savings — not an expense.",
        personal_use: "Money you take for yourself. Not an expense: net revenue does not change.",
        correction: "Fixing a mistake. Not an expense.",
      },
      amount: "Amount",
      expenseKind: "Kind of expense",
      expenseWhat: "What was it for?",
      takingHomeHint: "What you take home is added to your savings.",
      addChange: "Put change in",
      takeOut: "Take money out",
      why: "Why (optional)",
      record: "Record",
      closeDay: "Close the day",
      countHint: "Count everything in the drawer and type the total. You will not be shown what it should be.",
      counted: "Counted ({currency})",
      takingHome: "Taking home ({currency})",
      leftFor: "{amount} stays in the drawer for tomorrow.",
      closeNow: "Close the drawer",
      closed: "Drawer closed",
      result: {
        counted: "Counted",
        takenOut: "Taken out",
        left: "Left in the drawer",
        expected: "It should have held",
        difference: "Difference",
      },
      failed: "Could not record that.",
    },
    tab: {
      label: "Whose tab?",
      placeholder: "Name, or the end of their phone number",
      open: "Open a new tab for “{name}”",
      newTab: "New tab — opened with this sale",
      owes: "Already owes {amount}",
      nothingOwed: "Owes nothing right now",
      change: "Change",
    },
    chips: {
      all: "All",
      top: "Top sellers",
    },
    receipt: {
      savedOnDevice: "Saved on this device",
      completed: "Sale completed",
      offlineNote:
        "No connection — it syncs automatically once you are back online",
      receiptNo: "Receipt #{number}",
      recorded: "Recorded",
      changeBack: "Change to give back",
      staysOnCredit: "Stays on credit",
      totalWith: "Total · {method}",
      print: "🖨 Print",
      next: "Next customer",
      printTotal: "TOTAL",
      printChange: "Change",
      printOnCredit: "On credit",
      thanks: "Thank you!",
    },
    recent: {
      title: "Today at the register",
      subtitle: "Totals, latest sales and anything still to sync",
      takings: "Takings",
      sales: "Sales",
      average: "Average",
      collectedOnTabs: "Paid on tabs",
      putOnCredit: "Put on credit",
      owedTotal: "Owed in total",
      takingsHint: "Money collected today. Credit only counts once it is paid.",
      pending: {
        one: "{count} sale saved on this device, waiting for the connection to come back. It syncs automatically.",
        other:
          "{count} sales saved on this device, waiting for the connection to come back. They sync automatically.",
      },
      rejected: "Rejected by the server — record these manually",
      lines: { one: "{count} line", other: "{count} lines" },
      latest: "Latest sales",
      noneToday: "No sale rung up yet today",
      sale: "Sale",
    },
    held: {
      title: "Held tickets",
      subtitle: "Baskets parked while other customers are served",
      nothing: "Nothing on hold",
      nothingHint:
        "Park a ticket to serve the next customer without losing the basket",
      justNow: "just now",
      minAgo: "{count} min ago",
      discard: "Discard",
      resume: "Resume",
      ticket: "Ticket",
    },
  },

  fr: {
    loading: "Ouverture de la caisse…",
    catalogError:
      "Impossible de charger le catalogue. Vérifiez la connexion et réessayez.",
    saleFailed:
      "La vente n'a pas pu être enregistrée. Vérifiez le ticket et réessayez.",
    offlineQueued: {
      one: "📴 {count} vente enregistrée sur cet appareil — synchronisation au retour de la connexion",
      other:
        "📴 {count} ventes enregistrées sur cet appareil — synchronisation au retour de la connexion",
    },
    oversell: {
      title: "Rupture de stock",
      message:
        "{name} n'a plus de stock. Le vendre quand même ? Son stock passera en négatif pour que vous puissiez corriger le comptage plus tard.",
      confirm: "Vendre quand même",
    },
    clear: {
      title: "Vider le ticket ?",
      message: "Toutes les lignes du ticket en cours seront supprimées.",
      confirm: "Vider le ticket",
    },
    header: {
      drawer: "Tiroir-caisse",
      supplier: "Livraison ou paiement d'un fournisseur",
      register: "Caisse",
      today: "aujourd'hui",
      todayAria: "Aujourd'hui : {sales}, {amount}. Ouvrir les ventes du jour",
      pickups: "Commandes à retirer",
      pickupsDue: {
        one: "{count} retrait prévu aujourd'hui",
        other: "{count} retraits prévus aujourd'hui",
      },
      held: "Tickets en attente",
      lock: "Verrouiller la caisse",
      lockAs: "Verrouiller la caisse ({name})",
      showActions: "Afficher les boutons — {count} en attente",
      manage: "Gérer la boutique (connexion admin)",
      manageShort: "Gérer",
    },
    lock: {
      title: "Déverrouiller la caisse",
      prompt: "Saisissez votre code PIN pour ouvrir la caisse",
      notRecognised: "Ce code PIN n'est pas reconnu",
      checkFailed: "Impossible de vérifier ce code — la connexion fonctionne-t-elle ?",
      checking: "Vérification…",
      deleteDigit: "Effacer le dernier chiffre",
      noPin:
        "Pas encore de code PIN ? Le propriétaire en attribue un à chacun dans Gérer la boutique → Équipe.",
      manage: "⚙️ Gérer la boutique",
      registerFallback: "Caisse",
    },
    pickups: {
      title: "À retirer",
      loadFailed: "Impossible de charger les commandes en attente.",
      empty: "Aucune commande à retirer.",
      customer: "Client",
      at: "{day} à {time}",
      dueToday: "Aujourd'hui",
      late: { one: "{count} jour de retard", other: "{count} jours de retard" },
      confirm: "Encaisser {amount} et remettre la commande ?",
      paidBy: "Payé par",
      handedOver: "Remis",
      notYet: "Pas encore",
      handOver: "Remettre la commande",
      collectFailed:
        "Impossible de remettre la commande — vérifiez que le stock est là.",
      statusPreparing: "En préparation",
      statusReady: "Prête",
      markReady: "Marquer comme prête",
      markReadyFailed: "Impossible de marquer la commande comme prête — réessayez.",
    },
    ticket: {
      title: "Ticket en cours",
      hold: "⏸ Attente",
      clear: "🗑 Vider",
      empty: "Ticket vide",
      emptyHint: "Touchez un produit pour commencer la vente",
      notInStock: "pas en stock",
      remove: "retirer",
      removeOne: "Retirer un {name}",
      addOne: "Ajouter un {name}",
      subtotal: "Sous-total · {items}",
      offers: { one: "{count} offre", other: "{count} offres" },
      discount: "Remise",
      none: "Aucune",
      total: "Total",
    },
    payment: {
      cashReceived: "Espèces reçues",
      change: "Monnaie",
      exact: "Exact",
      short: "Le montant reçu est inférieur au total",
      creditNote: "Rien n'est payé maintenant — cela s'ajoute à son compte.",
      creditPartialNote: "Une partie est payée maintenant — le reste s'ajoute à son compte.",
      creditFor: "À crédit pour {name}.",
      creditReceivedNow: "Reçu maintenant",
      creditStays: "Reste à crédit",
      showKeypad: "Afficher le clavier",
      hideKeypad: "Masquer le clavier",
      typeCash: "Saisir le montant reçu",
      complete: "Valider la vente",
      saving: "Enregistrement…",
    },
    cart: {
      noItems: "Aucun article",
      tapProduct: "Touchez un produit pour commencer",
      review: "Touchez pour voir le ticket",
      charge: "Encaisser",
    },
    customer: {
      add: "Ajouter une carte client",
      purchasesBefore: {
        one: "{count} achat ici auparavant",
        other: "{count} achats ici auparavant",
      },
      firstPurchase: "Premier achat ici",
      owes: "Doit {amount}",
      linked: "Liée à cette vente",
      scanTitle: "Carte client",
      notRecognised: "Cette carte n'est pas reconnue",
      checkFailed: "Impossible de vérifier cette carte",
      nobody: "Personne n'a été trouvé avec ce numéro",
      searchFailed: "Recherche impossible",
      pointCamera: "Visez le code du client",
      cameraDenied: "L'accès à la caméra n'a pas été autorisé — saisissez le code à la place.",
      cameraUnsupported: "Cet appareil n'a pas de caméra utilisable — saisissez le code à la place.",
      cardCode: "Code de la carte",
      codePlaceholder: "8 caractères",
      find: "Trouver",
      phoneDigits: "…ou les derniers chiffres de son téléphone",
      phonePlaceholder: "ex. 5432",
      searchButton: "Rechercher",
      typeInstead: "Le saisir",
      useCamera: "Utiliser la caméra",
      skip: "Passer",
    },
    search: {
      placeholder: "Produit, marque ou saveur…",
      clear: "Effacer la recherche",
    },
    grid: {
      noMatch: "Aucun produit ne correspond",
      nothingFor: "Rien trouvé pour « {term} »",
      emptyCategory: "Cette catégorie n'a pas encore de produits",
      shelfEmpty: "Votre rayon est vide",
      shelfEmptyHint: "Ajoutez d'abord vos produits, puis revenez ici pour les vendre. Choisir dans le catalogue prend une minute.",
      addProducts: "Ajouter des produits",
    },
    packChoice: {
      subtitle: "Choisissez ce que veut le client pour chaque partie.",
      included: "Inclus",
      chooseOne: "Choisir un",
      chooseMany: "Choisir {count}",
      picked: "{picked} sur {total}",
      left: "{count} en rayon",
      out: "Épuisé",
      less: "Retirer un {name}",
      add: "Ajouter au ticket",
      pickAll: "Tout choisir d'abord",
    },
    group: {
      open: "Ouvrir {name}, {count} variantes",
      variants: { one: "{count} variante", other: "{count} variantes" },
      attention: "{count} bas ou épuisé(s)",
      from: "dès",
      inStock: "{count} en stock",
      summary: {
        one: "{count} variante · {stock} en stock",
        other: "{count} variantes · {stock} en stock",
      },
      removeOne: "Retirer un {name}",
      done: "Terminé",
      doneWithCount: "Terminé · {count} ajouté(s)",
    },
    tile: {
      add: "Ajouter {name}, {price}",
      out: "Épuisé",
      left: "{count} restants",
      offer: "OFFRE",
      buyGet: "{buy} achetés, {get} offerts",
      packOf: { one: "Pack · {count} article", other: "Pack · {count} articles" },
    },
    belowCost: {
      title: "Prix trop bas",
      message: "À cette remise, {names} serait vendu moins cher que ce que la boutique l'a payé.",
      askOwner: "Un propriétaire peut valider cette vente avec son code PIN.",
      ownerOnly: "Seul un propriétaire peut valider ceci.",
      changeDiscount: "Modifier la remise",
    },
    supplier: {
      title: "Fournisseur",
      subtitle: "Du stock arrivé, ou de l'argent sorti du tiroir.",
      delivery: "Livraison",
      payOnly: "Paiement seul",
      who: "Fournisseur",
      whoPlaceholder: "De qui ?",
      unknown: "Choisissez un fournisseur de la liste pour le payer.",
      searchProduct: "Trouver un produit…",
      new: "Nouveau",
      lotTotal: "Total du lot ({currency})",
      amount: "Montant sorti du tiroir ({currency})",
      paidFromDrawer: "Payé maintenant, depuis le tiroir ({currency})",
      paidHint: "Laissez vide si rien n'a été payé.",
      note: "Note",
      recordDelivery: "Enregistrer la livraison",
      recordPayout: "Enregistrer le paiement",
      received: "Livraison enregistrée — le stock est à jour.",
      paid: "Paiement à {name} enregistré.",
      failed: "Impossible d'enregistrer.",
    },
    drawer: {
      title: "Tiroir-caisse",
      openSince: "Ouvert depuis {time} · {name}",
      openTitle: "Ouvrir le tiroir",
      openHint: "Comptez l'argent du tiroir et tapez le total avant la première vente.",
      open: "Ouvrir le tiroir",
      shouldHold: "Devrait être dans le tiroir",
      lines: {
        opening: "Compté à l'ouverture",
        sales: { one: "Espèces de {count} vente", other: "Espèces de {count} ventes" },
      },
      movements: {
        cash_in: "Monnaie ajoutée",
        withdrawal: "Retrait",
        refund: "Remboursement en espèces",
        tab_payment: "Compte réglé",
        supplier_payout: "Fournisseur payé",
      },
      reasonTitle: "C'est pour quoi ?",
      reasons: {
        float: "Monnaie pour la caisse",
        owner_investment: "Mon propre argent",
        from_safe: "Depuis mon épargne",
        expense: "Payer une dépense",
        supplier: "Payer un fournisseur",
        to_safe: "Mettre dans mon épargne",
        personal_use: "Usage personnel",
        correction: "Correction",
      },
      reasonHints: {
        float: "Pièces et billets pour rendre la monnaie. Ce n'est pas un revenu.",
        owner_investment: "De l'argent que vous ajoutez de votre poche. Ce n'est pas une vente.",
        from_safe: "Pris dans votre épargne. Votre épargne baisse d'autant.",
        expense: "Loyer, électricité, ménage… Compté comme dépense : le résultat net baisse.",
        supplier: "Ouvre le paiement fournisseur : ce que vous lui devez baisse.",
        to_safe: "Argent que vous emportez pour le garder. Il devient de l'épargne — pas une dépense.",
        personal_use: "Argent que vous prenez pour vous. Pas une dépense : le résultat net ne change pas.",
        correction: "Corriger une erreur. Pas une dépense.",
      },
      amount: "Montant",
      expenseKind: "Type de dépense",
      expenseWhat: "C'était pour quoi ?",
      takingHomeHint: "Ce que vous emportez est ajouté à votre épargne.",
      addChange: "Ajouter de la monnaie",
      takeOut: "Retirer de l'argent",
      why: "Pourquoi (facultatif)",
      record: "Enregistrer",
      closeDay: "Clôturer la journée",
      countHint: "Comptez tout ce qu'il y a dans le tiroir et tapez le total. Le montant attendu ne vous sera pas montré.",
      counted: "Compté ({currency})",
      takingHome: "Emporté ({currency})",
      leftFor: "{amount} reste dans le tiroir pour demain.",
      closeNow: "Fermer le tiroir",
      closed: "Tiroir fermé",
      result: {
        counted: "Compté",
        takenOut: "Emporté",
        left: "Laissé dans le tiroir",
        expected: "Il aurait dû contenir",
        difference: "Écart",
      },
      failed: "Impossible d'enregistrer.",
    },
    tab: {
      label: "Sur le compte de qui ?",
      placeholder: "Nom, ou fin du numéro de téléphone",
      open: "Ouvrir un compte pour « {name} »",
      newTab: "Nouveau compte — ouvert avec cette vente",
      owes: "Doit déjà {amount}",
      nothingOwed: "Ne doit rien pour l'instant",
      change: "Changer",
    },
    chips: {
      all: "Tout",
      top: "Meilleures ventes",
    },
    receipt: {
      savedOnDevice: "Enregistrée sur cet appareil",
      completed: "Vente validée",
      offlineNote:
        "Pas de connexion — la synchronisation se fera automatiquement dès le retour en ligne",
      receiptNo: "Reçu n° {number}",
      recorded: "Enregistrée",
      changeBack: "Monnaie à rendre",
      staysOnCredit: "Reste à crédit",
      totalWith: "Total · {method}",
      print: "🖨 Imprimer",
      next: "Client suivant",
      printTotal: "TOTAL",
      printChange: "Monnaie",
      printOnCredit: "À crédit",
      thanks: "Merci !",
    },
    recent: {
      title: "Aujourd'hui en caisse",
      subtitle: "Totaux, dernières ventes et ce qui reste à synchroniser",
      takings: "Recette",
      sales: "Ventes",
      average: "Moyenne",
      collectedOnTabs: "Crédits réglés",
      putOnCredit: "Mis à crédit",
      owedTotal: "Total dû",
      takingsHint: "L'argent encaissé aujourd'hui. Un crédit ne compte qu'une fois payé.",
      pending: {
        one: "{count} vente enregistrée sur cet appareil, en attente de connexion. Elle se synchronisera automatiquement.",
        other:
          "{count} ventes enregistrées sur cet appareil, en attente de connexion. Elles se synchroniseront automatiquement.",
      },
      rejected: "Refusées par le serveur — à saisir manuellement",
      lines: { one: "{count} ligne", other: "{count} lignes" },
      latest: "Dernières ventes",
      noneToday: "Aucune vente aujourd'hui pour l'instant",
      sale: "Vente",
    },
    held: {
      title: "Tickets en attente",
      subtitle: "Paniers mis de côté pendant que d'autres clients sont servis",
      nothing: "Aucun ticket en attente",
      nothingHint:
        "Mettez un ticket en attente pour servir le client suivant sans perdre le panier",
      justNow: "à l'instant",
      minAgo: "il y a {count} min",
      discard: "Supprimer",
      resume: "Reprendre",
      ticket: "Ticket",
    },
  },

  ar: {
    loading: "جارٍ فتح نقطة البيع…",
    catalogError: "تعذّر تحميل الكتالوج. تحقّق من الاتصال وأعد المحاولة.",
    saleFailed: "تعذّر تسجيل عملية البيع. تحقّق من التذكرة وحاول مجدداً.",
    offlineQueued: {
      one: "📴 عملية بيع واحدة محفوظة على هذا الجهاز — ستُزامَن عند عودة الاتصال",
      two: "📴 عمليتا بيع محفوظتان على هذا الجهاز — ستُزامَنان عند عودة الاتصال",
      few: "📴 {count} عمليات بيع محفوظة على هذا الجهاز — ستُزامَن عند عودة الاتصال",
      other:
        "📴 {count} عملية بيع محفوظة على هذا الجهاز — ستُزامَن عند عودة الاتصال",
    },
    oversell: {
      title: "نفد من المخزون",
      message:
        "لم يتبقَّ أي مخزون من {name}. هل تريد بيعه على أي حال؟ سيصبح مخزونه سالباً لتتمكّن من تصحيح الجرد لاحقاً.",
      confirm: "بيعه على أي حال",
    },
    clear: {
      title: "إفراغ التذكرة؟",
      message: "ستتم إزالة جميع أسطر التذكرة الحالية.",
      confirm: "إفراغ التذكرة",
    },
    header: {
      drawer: "درج النقود",
      supplier: "توريد أو دفعة لمورد",
      register: "نقطة البيع",
      today: "اليوم",
      todayAria: "اليوم: {sales}، {amount}. فتح مبيعات اليوم",
      pickups: "طلبات بانتظار الاستلام",
      pickupsDue: {
        one: "استلام واحد مستحق اليوم",
        two: "استلامان مستحقان اليوم",
        few: "{count} عمليات استلام مستحقة اليوم",
        other: "{count} عملية استلام مستحقة اليوم",
      },
      held: "التذاكر المعلّقة",
      lock: "قفل نقطة البيع",
      lockAs: "قفل نقطة البيع ({name})",
      showActions: "إظهار الأزرار — {count} بانتظارك",
      manage: "إدارة المتجر (دخول المسؤول)",
      manageShort: "إدارة",
    },
    lock: {
      title: "فتح قفل نقطة البيع",
      prompt: "أدخل رمزك السري لفتح نقطة البيع",
      notRecognised: "لم يتم التعرّف على هذا الرمز",
      checkFailed: "تعذّر التحقق من الرمز — هل الاتصال يعمل؟",
      checking: "جارٍ التحقق…",
      deleteDigit: "حذف آخر رقم",
      noPin:
        "لا تملك رمزاً بعد؟ يمنح المالك كل شخص رمزاً من إدارة المتجر ← الفريق.",
      manage: "⚙️ إدارة المتجر",
      registerFallback: "نقطة البيع",
    },
    pickups: {
      title: "بانتظار الاستلام",
      loadFailed: "تعذّر تحميل الطلبات المنتظرة.",
      empty: "لا توجد طلبات بانتظار الاستلام.",
      customer: "زبون",
      at: "{day} الساعة {time}",
      dueToday: "مستحق اليوم",
      late: {
        one: "متأخر يوماً واحداً",
        two: "متأخر يومين",
        few: "متأخر {count} أيام",
        other: "متأخر {count} يوماً",
      },
      confirm: "استلام {amount} وتسليم الطلب؟",
      paidBy: "طريقة الدفع",
      handedOver: "تم التسليم",
      notYet: "ليس بعد",
      handOver: "تسليم الطلب",
      collectFailed: "تعذّر تسليم الطلب — تأكّد من توفر المخزون.",
      statusPreparing: "قيد التحضير",
      statusReady: "جاهز",
      markReady: "تحديد كجاهز",
      markReadyFailed: "تعذّر تحديد الطلب كجاهز — حاول مرة أخرى.",
    },
    ticket: {
      title: "التذكرة الحالية",
      hold: "⏸ تعليق",
      clear: "🗑 إفراغ",
      empty: "تذكرة فارغة",
      emptyHint: "اضغط على منتج لبدء البيع",
      notInStock: "غير متوفر",
      remove: "إزالة",
      removeOne: "إزالة واحدة من {name}",
      addOne: "إضافة واحدة من {name}",
      subtotal: "المجموع الفرعي · {items}",
      offers: {
        one: "عرض واحد",
        two: "عرضان",
        few: "{count} عروض",
        other: "{count} عرض",
      },
      discount: "خصم",
      none: "بدون",
      total: "المجموع",
    },
    payment: {
      cashReceived: "المبلغ المستلم",
      change: "الباقي",
      exact: "بالضبط",
      short: "المبلغ المستلم أقل من المجموع",
      creditNote: "لا يُدفع شيء الآن — يُضاف هذا إلى دينه.",
      creditPartialNote: "يُدفع جزء الآن — والباقي يُضاف إلى دينه.",
      creditFor: "بالدين لـ {name}.",
      creditReceivedNow: "المستلم الآن",
      creditStays: "يبقى ديناً",
      showKeypad: "إظهار لوحة الأرقام",
      hideKeypad: "إخفاء لوحة الأرقام",
      typeCash: "إدخال المبلغ المستلم",
      complete: "إتمام البيع",
      saving: "جارٍ الحفظ…",
    },
    cart: {
      noItems: "لا منتجات بعد",
      tapProduct: "اضغط على منتج للبدء",
      review: "اضغط لمراجعة التذكرة",
      charge: "تحصيل",
    },
    customer: {
      add: "إضافة بطاقة الزبون",
      purchasesBefore: {
        one: "عملية شراء سابقة واحدة هنا",
        two: "عمليتا شراء سابقتان هنا",
        few: "{count} عمليات شراء سابقة هنا",
        other: "{count} عملية شراء سابقة هنا",
      },
      firstPurchase: "أول عملية شراء هنا",
      owes: "يدين بـ {amount}",
      linked: "مرتبطة بهذا البيع",
      scanTitle: "بطاقة الزبون",
      notRecognised: "لم يتم التعرّف على هذه البطاقة",
      checkFailed: "تعذّر التحقق من البطاقة",
      nobody: "لا أحد بهذا الرقم",
      searchFailed: "تعذّر البحث",
      pointCamera: "وجّه الكاميرا نحو رمز الزبون",
      cameraDenied: "لم يُسمح بالوصول إلى الكاميرا — أدخل الرمز يدوياً بدلاً من ذلك.",
      cameraUnsupported: "لا توجد كاميرا صالحة للاستخدام على هذا الجهاز — أدخل الرمز يدوياً بدلاً من ذلك.",
      cardCode: "رمز البطاقة",
      codePlaceholder: "8 أحرف",
      find: "بحث",
      phoneDigits: "…أو آخر أرقام هاتفه",
      phonePlaceholder: "مثال: 5432",
      searchButton: "بحث",
      typeInstead: "كتابته يدوياً",
      useCamera: "استخدام الكاميرا",
      skip: "تخطٍّ",
    },
    search: {
      placeholder: "ابحث عن منتج أو علامة أو نكهة…",
      clear: "مسح البحث",
    },
    grid: {
      noMatch: "لا يوجد منتج مطابق",
      nothingFor: "لا نتائج لـ «{term}»",
      emptyCategory: "لا توجد منتجات في هذه الفئة بعد",
      shelfEmpty: "رفّك فارغ",
      shelfEmptyHint: "أضف منتجاتك أولًا ثم عد إلى هنا لبيعها. الاختيار من الكتالوج يستغرق دقيقة.",
      addProducts: "إضافة منتجات",
    },
    packChoice: {
      subtitle: "اختر ما يريده الزبون لكل جزء.",
      included: "مشمول",
      chooseOne: "اختر واحداً",
      chooseMany: "اختر {count}",
      picked: "{picked} من {total}",
      left: "المتبقي {count}",
      out: "نفد",
      less: "إزالة واحد من {name}",
      add: "إضافة إلى التذكرة",
      pickAll: "اختر كل شيء أولاً",
    },
    group: {
      open: "فتح {name}، {count} خيارات",
      variants: {
        one: "خيار واحد",
        two: "خياران",
        few: "{count} خيارات",
        many: "{count} خياراً",
        other: "{count} خيار",
      },
      attention: "{count} قليل أو نافد",
      from: "من",
      inStock: "{count} في المخزون",
      summary: {
        one: "خيار واحد · {stock} في المخزون",
        two: "خياران · {stock} في المخزون",
        few: "{count} خيارات · {stock} في المخزون",
        many: "{count} خياراً · {stock} في المخزون",
        other: "{count} خيار · {stock} في المخزون",
      },
      removeOne: "إزالة واحدة من {name}",
      done: "تم",
      doneWithCount: "تم · أُضيف {count}",
    },
    tile: {
      add: "إضافة {name}، {price}",
      out: "نفد",
      left: "بقي {count}",
      offer: "عرض",
      buyGet: "اشترِ {buy} واحصل على {get} مجاناً",
      packOf: {
        zero: "باقة · بدون منتجات",
        one: "باقة · منتج واحد",
        two: "باقة · منتجان",
        few: "باقة · {count} منتجات",
        many: "باقة · {count} منتجاً",
        other: "باقة · {count} منتج",
      },
    },
    belowCost: {
      title: "السعر منخفض جداً",
      message: "بهذا التخفيض سيُباع {names} بأقل مما دفعه المتجر.",
      askOwner: "يمكن للمالك الموافقة على هذه العملية برمزه.",
      ownerOnly: "المالك وحده يمكنه الموافقة على هذا.",
      changeDiscount: "تغيير التخفيض",
    },
    supplier: {
      title: "المورد",
      subtitle: "بضاعة وصلت، أو مال خرج من الدرج.",
      delivery: "توريد",
      payOnly: "دفع فقط",
      who: "المورد",
      whoPlaceholder: "ممن؟",
      unknown: "اختر مورداً من القائمة للدفع له.",
      searchProduct: "ابحث عن منتج…",
      new: "جديد",
      lotTotal: "مجموع الدفعة ({currency})",
      amount: "المبلغ المدفوع من الدرج ({currency})",
      paidFromDrawer: "المدفوع الآن من الدرج ({currency})",
      paidHint: "اتركه فارغاً إن لم يُدفع شيء.",
      note: "ملاحظة",
      recordDelivery: "تسجيل التوريد",
      recordPayout: "تسجيل الدفعة",
      received: "تم تسجيل التوريد — المخزون محدّث.",
      paid: "تم تسجيل الدفعة لـ {name}.",
      failed: "تعذّر التسجيل.",
    },
    drawer: {
      title: "درج النقود",
      openSince: "مفتوح منذ {time} · {name}",
      openTitle: "افتح الدرج",
      openHint: "عدّ النقود في الدرج واكتب المجموع قبل أول عملية بيع.",
      open: "فتح الدرج",
      shouldHold: "يجب أن يكون في الدرج",
      lines: {
        opening: "المعدود عند الفتح",
        sales: { zero: "نقد من المبيعات", one: "نقد من عملية بيع واحدة", two: "نقد من عمليتي بيع", few: "نقد من {count} عمليات بيع", many: "نقد من {count} عملية بيع", other: "نقد من {count} عملية بيع" },
      },
      movements: {
        cash_in: "فكة مضافة",
        withdrawal: "سحب",
        refund: "استرداد نقدي",
        tab_payment: "تسديد دين",
        supplier_payout: "دفع لمورد",
      },
      reasonTitle: "لأي غرض؟",
      reasons: {
        float: "فكة للصندوق",
        owner_investment: "مالي الخاص",
        from_safe: "من مدخراتي",
        expense: "دفع مصروف",
        supplier: "دفع لمورّد",
        to_safe: "نقل إلى مدخراتي",
        personal_use: "استعمال شخصي",
        correction: "تصحيح",
      },
      reasonHints: {
        float: "قطع وأوراق لإرجاع الباقي. ليست دخلاً.",
        owner_investment: "مال تضيفه من جيبك. ليس عملية بيع.",
        from_safe: "يُؤخذ من مدخراتك، فتنقص بالمبلغ نفسه.",
        expense: "إيجار، كهرباء، تنظيف… يُحتسب مصروفاً فينقص صافي الإيراد.",
        supplier: "يفتح دفعة المورّد فينقص ما تدين به له.",
        to_safe: "نقود تأخذها لتحتفظ بها. تصبح مدخرات وليست مصروفاً.",
        personal_use: "مال تأخذه لنفسك. ليس مصروفاً: صافي الإيراد لا يتغير.",
        correction: "تصحيح خطأ. ليس مصروفاً.",
      },
      amount: "المبلغ",
      expenseKind: "نوع المصروف",
      expenseWhat: "لأي غرض كان؟",
      takingHomeHint: "ما تأخذه إلى البيت يُضاف إلى مدخراتك.",
      addChange: "إضافة فكة",
      takeOut: "سحب نقود",
      why: "السبب (اختياري)",
      record: "تسجيل",
      closeDay: "إغلاق اليوم",
      countHint: "عدّ كل ما في الدرج واكتب المجموع. لن يُعرض عليك المبلغ المتوقع.",
      counted: "المعدود ({currency})",
      takingHome: "المأخوذ إلى البيت ({currency})",
      leftFor: "يبقى {amount} في الدرج للغد.",
      closeNow: "إغلاق الدرج",
      closed: "تم إغلاق الدرج",
      result: {
        counted: "المعدود",
        takenOut: "المسحوب",
        left: "المتبقي في الدرج",
        expected: "كان يجب أن يحتوي",
        difference: "الفرق",
      },
      failed: "تعذّر التسجيل.",
    },
    tab: {
      label: "على حساب من؟",
      placeholder: "الاسم أو آخر أرقام الهاتف",
      open: "فتح حساب جديد لـ «{name}»",
      newTab: "حساب جديد — يُفتح مع هذه العملية",
      owes: "عليه {amount}",
      nothingOwed: "لا شيء عليه حالياً",
      change: "تغيير",
    },
    chips: {
      all: "الكل",
      top: "الأكثر مبيعاً",
    },
    receipt: {
      savedOnDevice: "محفوظة على هذا الجهاز",
      completed: "تمت عملية البيع",
      offlineNote: "لا يوجد اتصال — ستُزامَن تلقائياً عند عودة الاتصال",
      receiptNo: "إيصال رقم {number}",
      recorded: "مسجّلة",
      changeBack: "الباقي المستحق للزبون",
      staysOnCredit: "يبقى ديناً",
      totalWith: "المجموع · {method}",
      print: "🖨 طباعة",
      next: "الزبون التالي",
      printTotal: "المجموع",
      printChange: "الباقي",
      printOnCredit: "بالدين",
      thanks: "شكراً لكم!",
    },
    recent: {
      title: "اليوم في نقطة البيع",
      subtitle: "المجاميع وآخر المبيعات وما لم تتم مزامنته بعد",
      takings: "الإيراد",
      sales: "المبيعات",
      average: "المتوسط",
      collectedOnTabs: "سداد الديون",
      putOnCredit: "بيع بالدين",
      owedTotal: "إجمالي المستحق",
      takingsHint: "المبلغ المحصَّل اليوم. لا يُحتسب الدين إلا بعد سداده.",
      pending: {
        one: "عملية بيع واحدة محفوظة على هذا الجهاز بانتظار عودة الاتصال، وستُزامَن تلقائياً.",
        two: "عمليتا بيع محفوظتان على هذا الجهاز بانتظار عودة الاتصال، وستُزامَنان تلقائياً.",
        few: "{count} عمليات بيع محفوظة على هذا الجهاز بانتظار عودة الاتصال، وستُزامَن تلقائياً.",
        other:
          "{count} عملية بيع محفوظة على هذا الجهاز بانتظار عودة الاتصال، وستُزامَن تلقائياً.",
      },
      rejected: "رفضها الخادم — سجّلها يدوياً",
      lines: {
        one: "سطر واحد",
        two: "سطران",
        few: "{count} أسطر",
        other: "{count} سطراً",
      },
      latest: "آخر المبيعات",
      noneToday: "لم تُسجَّل أي عملية بيع اليوم بعد",
      sale: "بيع",
    },
    held: {
      title: "التذاكر المعلّقة",
      subtitle: "سلال محفوظة جانباً أثناء خدمة زبائن آخرين",
      nothing: "لا توجد تذاكر معلّقة",
      nothingHint: "علّق تذكرة لخدمة الزبون التالي دون فقدان السلة",
      justNow: "الآن",
      minAgo: "منذ {count} د",
      discard: "حذف",
      resume: "استئناف",
      ticket: "تذكرة",
    },
  },
};

export default pos;
